/**
 * What the printer is set to, in one place.
 *
 * The settings sheet used to own this privately, which meant the only code
 * that knew a vendor had chosen the paired 40x12 roll was the screen they
 * chose it on. The print itself ran from the rounding screen and passed no
 * size at all, so every run fell back to the single 40x12 default: the pair
 * sensor read two labels as one, printed the top half and fed the bottom out
 * blank. Half the roll, every run, with the setting sitting right there.
 *
 * So the preference lives in the service that does the printing, and both the
 * screen that sets it and the code that prints read the same answer.
 */

import { LABEL_SIZES, DEFAULT_SIZE_KEY, DEFAULT_FIELDS } from './label';

const KEY = 'cc:labelPrinter:prefs';

/** The stored choices, as keys. Defaults for anything missing or unreadable. */
export function readPrefs() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const p = raw ? JSON.parse(raw) : null;
    return {
      fields: Array.isArray(p?.fields) ? p.fields : DEFAULT_FIELDS,
      sizeKey: p?.sizeKey || DEFAULT_SIZE_KEY,
    };
  } catch {
    return { fields: DEFAULT_FIELDS, sizeKey: DEFAULT_SIZE_KEY };
  }
}

/** @param {{fields:string[], sizeKey:string}} prefs */
export function writePrefs(prefs) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({
      fields: Array.isArray(prefs?.fields) ? prefs.fields : DEFAULT_FIELDS,
      sizeKey: prefs?.sizeKey || DEFAULT_SIZE_KEY,
    }));
  } catch {
    /* a remembered setting is a convenience, never a requirement */
  }
}

/** Find a size by key, falling back to the default rather than to undefined. */
export function sizeForKey(key) {
  return LABEL_SIZES.find((s) => s.key === key)
    || LABEL_SIZES.find((s) => s.key === DEFAULT_SIZE_KEY);
}

/**
 * The stored choices resolved into what `printLabels` takes. Pass the result
 * straight through; never call printLabels without it, or the size goes
 * undefined and a paired roll silently prints one up.
 *
 * @returns {{fields:string[], size:object}}
 */
export function labelPrefs() {
  const p = readPrefs();
  return { fields: p.fields, size: sizeForKey(p.sizeKey) };
}
